import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo
} from 'react';
import {
  Dialog,
  Box,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Divider,
  Tooltip,
  Button,
  Collapse
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import PrintIcon from '@mui/icons-material/Print';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import moment from 'moment';
import { ThemeContext } from 'src/store/ContextStore';
import { NOTE_TYPE_COLOURS } from 'src/components/Constants';
import {
  getRecordDate,
  getRecordTitle,
  getSearchableText
} from './forms/formService';
import RecordCard from './RecordCard';

const SidebarItem = ({ label, count, dotColor, isSelected, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      px: 1,
      py: 0.5,
      borderRadius: 1,
      cursor: 'pointer',
      backgroundColor: isSelected ? 'action.selected' : 'transparent',
      '&:hover': { backgroundColor: 'action.hover' }
    }}
  >
    {dotColor !== undefined && (
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          backgroundColor: dotColor,
          flexShrink: 0
        }}
      />
    )}
    <Typography
      variant="body2"
      sx={{
        flex: 1,
        fontWeight: isSelected ? 'bold' : 'normal',
        fontSize: '0.875rem',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
      }}
    >
      {label}
    </Typography>
    {count != null && (
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        {count}
      </Typography>
    )}
  </Box>
);

const RecordBrowserModal = ({
  open,
  onClose,
  tiles = [],
  isLoading,
  patientName,
  patientId,
  patientDob,
  staffMemberOptions = [],
  // The member whose records these are — only used to resolve a PRENATAL event's subject label
  // (decision 21), so a fetal test never reads as something done to the patient.
  carrier
}) => {
  const { theme } = React.useContext(ThemeContext);
  const dateFormat = theme?.dateFormat || 'DD/MM/YYYY';

  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState(null);
  const [periodFilter, setPeriodFilter] = useState(null);
  const [sortOrder, setSortOrder] = useState('newest');
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [focusedIndex, setFocusedIndex] = useState(0);

  const searchRef = useRef(null);
  const cardRefs = useRef({});

  useEffect(() => {
    if (open) {
      setSearchQuery('');
      setTypeFilter(null);
      setPeriodFilter(null);
      setSortOrder('newest');
      setExpandedIds(new Set());
      setFocusedIndex(0);
    }
  }, [open]);

  const typeCounts = useMemo(() => {
    const counts = {};
    tiles.forEach((t) => {
      counts[t.noteType] = (counts[t.noteType] || 0) + 1;
    });
    return counts;
  }, [tiles]);

  const { years, minYear, hasUndatedRecords } = useMemo(() => {
    const yearSet = new Set();
    let hasUndated = false;
    tiles.forEach((t) => {
      const d = getRecordDate(t);
      if (d && moment(d).isValid()) {
        yearSet.add(moment(d).year());
      } else {
        hasUndated = true;
      }
    });
    const sorted = Array.from(yearSet).sort((a, b) => b - a);
    return {
      years: sorted,
      minYear: sorted.length ? sorted[sorted.length - 1] : null,
      hasUndatedRecords: hasUndated
    };
  }, [tiles]);

  const filteredRecords = useMemo(() => {
    let result = [...tiles];

    if (typeFilter) {
      result = result.filter((r) => r.noteType === typeFilter);
    }

    if (periodFilter !== null) {
      if (periodFilter === 'undated') {
        result = result.filter((r) => {
          const d = getRecordDate(r);
          return !d || !moment(d).isValid();
        });
      } else {
        const year = parseInt(periodFilter, 10);
        result = result.filter((r) => {
          const d = getRecordDate(r);
          return d && moment(d).isValid() && moment(d).year() === year;
        });
      }
    }

    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      result = result.filter((r) => getSearchableText(r).includes(q));
    }

    result.sort((a, b) => {
      const da = getRecordDate(a);
      const db = getRecordDate(b);
      if (!da && !db) return 0;
      if (!da) return 1;
      if (!db) return -1;
      const diff = moment(da).valueOf() - moment(db).valueOf();
      return sortOrder === 'newest' ? -diff : diff;
    });

    return result;
  }, [tiles, typeFilter, periodFilter, searchQuery, sortOrder]);

  useEffect(() => {
    if (focusedIndex >= filteredRecords.length && filteredRecords.length > 0) {
      setFocusedIndex(filteredRecords.length - 1);
    }
  }, [filteredRecords.length, focusedIndex]);

  useEffect(() => {
    const el = cardRefs.current[focusedIndex];
    if (el) el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [focusedIndex]);

  const toggleExpand = useCallback((id, e) => {
    const isShift = e?.shiftKey;
    setExpandedIds((prev) => {
      if (isShift) {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      }
      return prev.has(id) ? new Set() : new Set([id]);
    });
  }, []);

  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e) => {
      const tag = document.activeElement?.tagName;
      const inInput = tag === 'INPUT' || tag === 'TEXTAREA';

      if (e.key === 'ArrowDown' && !inInput) {
        e.preventDefault();
        const next = Math.min(focusedIndex + 1, filteredRecords.length - 1);
        setFocusedIndex(next);
        const record = filteredRecords[next];
        if (record) setExpandedIds(new Set([record._id]));
      } else if (e.key === 'ArrowUp' && !inInput) {
        e.preventDefault();
        const prev = Math.max(focusedIndex - 1, 0);
        setFocusedIndex(prev);
        const record = filteredRecords[prev];
        if (record) setExpandedIds(new Set([record._id]));
      } else if ((e.key === 'Enter' || e.key === ' ') && !inInput) {
        const record = filteredRecords[focusedIndex];
        if (record) {
          e.preventDefault();
          toggleExpand(record._id, e);
        }
      } else if ((e.ctrlKey || e.metaKey) && e.key === 'f') {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, focusedIndex, filteredRecords, toggleExpand]);

  const handlePrevNext = (direction) => {
    const next =
      direction === 'prev'
        ? Math.max(focusedIndex - 1, 0)
        : Math.min(focusedIndex + 1, filteredRecords.length - 1);
    setFocusedIndex(next);
    const record = filteredRecords[next];
    if (record) setExpandedIds(new Set([record._id]));
  };

  const patientLabel = [
    patientId || null,
    patientName || null,
    patientDob && moment(patientDob).isValid()
      ? moment(patientDob).format(dateFormat)
      : null
  ]
    .filter(Boolean)
    .join(' - ');

  const handlePrint = () => {
    const win = window.open('', '_blank');
    if (!win) return;

    const fmt = (d) =>
      d && moment(d).isValid() ? moment(d).format(dateFormat) : '';
    const findStaff = (id) =>
      staffMemberOptions?.find((s) => s.value === id)?.label || id;
    const esc = (s) =>
      String(s ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');

    const row = (label, value, pre) => {
      if (value == null || value === '') return '';
      return `<tr><td class="lbl">${esc(label)}</td><td${
        pre ? ' class="pre"' : ''
      }>${esc(value)}</td></tr>`;
    };

    const getDetailsHtml = (r) => {
      let rows = '';
      switch (r.noteType) {
        case 'Appointment':
          rows += row('Date', fmt(r.appointmentDate));
          rows += r.appointmentTime
            ? row('Time', moment(r.appointmentTime).format('HH:mm'))
            : '';
          rows += row('Clinic', r.clinicName);
          rows += row('Type', r.appointmentType);
          rows += row('Status', r.appointmentStatus);
          rows += row('Location', r.location);
          rows += r.staffMember?.length
            ? row('Staff', r.staffMember.map(findStaff).join(', '))
            : '';
          rows += row('Notes', r.appointmentNotes, true);
          break;
        case 'Referral':
          rows += row('Date', fmt(r.referralDate));
          rows += row('Type', r.referralType);
          rows += row('Referred To', r.referralTo);
          rows += row('Referred By', r.referredBy);
          rows += row('Status', r.referralStatus);
          rows += row('Triage', r.referralTriage);
          rows += row('Reason', r.referralReason, true);
          rows += row('Notes', r.referralNotes, true);
          rows += row('Date Closed', fmt(r.dateClosed));
          rows += row('Reason for Closure', r.reasonForClosure, true);
          break;
        case 'Test':
          rows += row('Test Name', r.testName);
          rows += row('Type', r.testType);
          rows += row('Date Requested', fmt(r.dateRequested));
          rows += row('Date Due', fmt(r.dateResultDue));
          rows += row('Date Received', fmt(r.dateResultReceived));
          rows += row('Lab', r.lab);
          rows +=
            r.ageWhenTested != null
              ? row('Age When Tested', r.ageWhenTested)
              : '';
          rows += row('Result', r.testResult, true);
          rows += row('Notes', r.testNotes, true);
          break;
        case 'Sample':
          rows += row('Date Taken', fmt(r.dateTaken));
          rows += row('Type', r.sampleType);
          rows += row('Laboratory', r.laboratory);
          rows += row('Status', r.sampleStatus);
          rows += row('Status Date', fmt(r.statusDate));
          rows += row('Held By', r.sampleHeldBy);
          rows += row('Tracking ID', r.trackingId);
          break;
        case 'Clinical Note':
          rows += row('Date', fmt(r.clinicalNoteDate));
          rows += row('Clinician', r.clinician);
          rows += row('Note', r.note, true);
          break;
        case 'Contact':
          rows += row('Date', fmt(r.contactDate));
          rows += row('Type', r.contactType);
          rows += row('Mode', r.contactMode);
          rows += row('Status', r.contactStatus);
          rows += row('Reason', r.reasonForContact, true);
          rows += r.staffMember?.length
            ? row('Staff', r.staffMember.map(findStaff).join(', '))
            : '';
          rows += row('Notes', r.contactNotes, true);
          break;
        case 'Consent':
          rows += row('Date', fmt(r.consentDate || r.consentRequestDate));
          rows +=
            r.consentRequestDate && r.consentDate
              ? row('Request Date', fmt(r.consentRequestDate))
              : '';
          rows += row('Type', r.consentType);
          rows += row('Response', r.response);
          rows += row('Given By', r.consentGivenBy);
          rows += row('Expiry', fmt(r.expiryDate));
          rows += row('Notes', r.consentNotes, true);
          break;
        case 'Treatment':
          rows += row('Start Date', fmt(r.treatmentDate));
          rows += row('End Date', fmt(r.treatmentEndDate));
          rows += row('Treatment', r.treatment, true);
          rows += row('Comment', r.comment, true);
          break;
        case 'Address':
          rows += row('Line 1', r.addressLine1);
          rows += row('Line 2', r.addressLine2);
          rows += row('Town/City', r.town || r.city);
          rows += row('Postcode', r.postCode);
          rows += row('Country', r.country);
          rows += r.primaryAddress ? row('Primary', 'Yes') : '';
          rows += r.correspondenceAddress ? row('Correspondence', 'Yes') : '';
          break;
        case 'Tissue Pathology':
          rows += row('Tissue Site', r.tissueSite);
          rows += row('Morphology', r.morphology);
          rows += row('Report Date', fmt(r.pathReportDate));
          rows += r.staffMember?.length
            ? row('Staff', r.staffMember.map(findStaff).join(', '))
            : '';
          rows += r.pathologyDoctor
            ? row('Pathologist', findStaff(r.pathologyDoctor))
            : '';
          break;
        case 'Disease':
          rows += row('ID', r.id);
          rows += row('Age at diagnosis', r.age);
          rows += row('Date reported', fmt(r.dateReported));
          rows += row(
            'Clinically confirmed',
            r.clinicallyConfirmed ? 'Yes' : 'No'
          );
          break;
        case 'Phenotype':
          rows += row('ID', r.id);
          rows += row('Age reported', r.recordedAge);
          rows += row('Date recorded', fmt(r.dateRecorded));
          rows += row(
            'Clinically confirmed',
            r.clinicallyConfirmed ? 'Yes' : 'No'
          );
          break;
        case 'Genotype':
          rows += row('ID', r.id);
          rows += row('Carrier status', r.carrierStatus);
          rows += row('Age reported', r.recordedAge);
          rows += row('Date recorded', fmt(r.dateRecorded));
          rows += row(
            'Clinically confirmed',
            r.clinicallyConfirmed ? 'Yes' : 'No'
          );
          break;
        case 'Pregnancy': {
          const outcomeLabels = {
            ongoing: 'Ongoing',
            sab: 'Miscarriage (SAB)',
            ectopic: 'Ectopic (ECT)',
            top: 'Termination (TOP)',
            stillbirth: 'Stillbirth (SB)',
            liveBirth: 'Live birth'
          };
          const conceivedLabels = {
            partner: 'Partner',
            spermDonor: 'Sperm donor',
            eggDonor: 'Egg donor',
            embryoDonor: 'Donated embryo',
            partnerAndDonor: 'Partner + donor'
          };
          rows += row('Outcome', outcomeLabels[r.outcome] || r.outcome);
          rows +=
            r.gestationalWeeks != null && r.gestationalWeeks !== ''
              ? row('Gestational age', `${r.gestationalWeeks} weeks`)
              : '';
          rows += row(
            'Conceived with',
            conceivedLabels[r.conceivedWith] || r.conceivedWith
          );
          rows +=
            r.fetalSexKnown && r.fetalSex
              ? row('Fetal sex', r.fetalSex === 'male' ? 'Male' : 'Female')
              : '';
          rows += row('Karyotype / finding', r.karyotype);
          rows += row('Note', r.comments, true);
          rows += r.hidden ? row('Visibility', 'Hidden from pedigree') : '';
          break;
        }
        case 'Risk Score':
          rows += row('Type', r.riskLabel);
          if (r.recordType === 'gail' && r.gailRiskScore != null)
            rows += row('Risk score', r.gailRiskScore);
          if (r.recordType === 'canrisk') {
            try {
              const pr = r.canriskResult?.pedigree_result?.[0];
              const tenYr =
                pr?.ten_yr_cancer_risk?.[0]?.['breast cancer risk']?.percent;
              const lifetime =
                pr?.lifetime_cancer_risk?.[0]?.['breast cancer risk']?.percent;
              if (tenYr != null && lifetime != null) {
                rows += row(
                  'Result',
                  `10yr: ${tenYr}% | Lifetime: ${lifetime}%`
                );
              }
            } catch (_) {
              /* ignore */
            }
          }
          if (r.recordType === 'prs' && r.prsResult) {
            rows += row('Trait', r.prsResult.trait || r.prsResult.pgsId);
            if (r.prsResult.zscore != null)
              rows += row('Z-score', r.prsResult.zscore);
            if (r.prsResult.percentile != null)
              rows += row('Percentile', `${r.prsResult.percentile}%`);
          }
          break;
        default:
          break;
      }
      if (!rows) return '';
      return `<table class="details"><tbody>${rows}</tbody></table>`;
    };

    const cards = filteredRecords
      .map((r) => {
        const d = getRecordDate(r);
        const date = fmt(d);
        const typeColor = NOTE_TYPE_COLOURS[r.noteType] || '#888';
        const isExpanded = expandedIds.has(r._id);
        const details = isExpanded ? getDetailsHtml(r) : '';
        return `<div class="card${
          isExpanded ? ' expanded' : ''
        }" style="border-left:3px solid ${typeColor}">
          <div class="card-hdr">
            <span class="type-label" style="color:${typeColor}">${esc(
          r.noteType
        )}</span>
            <span class="date">${esc(date)}</span>
          </div>
          <div class="title">${esc(getRecordTitle(r))}</div>
          ${details ? `<div class="card-body">${details}</div>` : ''}
        </div>`;
      })
      .join('');

    win.document.write(
      `<html><head><title>Clinical Records${
        patientLabel ? ' - ' + patientLabel.replace(/\//g, '-') : ''
      }</title>
      <style>
        body{font-family:sans-serif;padding:20px;color:#333}
        h2{margin:0 0 4px}
        .meta{margin:0 0 16px;color:#666;font-size:13px}
        .card{border:1px solid #e0e0e0;border-radius:6px;margin-bottom:10px;overflow:hidden;page-break-inside:avoid}
        .card-hdr{display:flex;align-items:center;justify-content:space-between;padding:8px 12px 0 12px}
        .card.expanded .card-body{border-top:1px solid #e0e0e0}
        .type-label{font-size:10px;font-weight:700;letter-spacing:0.06em;text-transform:uppercase}
        .title{padding:2px 12px 8px;font-size:13px;font-weight:500}
        .date{font-size:12px;font-weight:700;white-space:nowrap}
        .card-body{padding:8px 12px}
        table.details{border-collapse:collapse;font-size:12px;width:100%}
        table.details td{padding:3px 8px 3px 0;vertical-align:top}
        table.details td.lbl{font-weight:600;color:#555;width:140px;white-space:nowrap}
        table.details td.pre{white-space:pre-wrap}
      </style>
      </head><body>
      <h2>Clinical Records${patientLabel ? ` — ${patientLabel}` : ''}</h2>
      ${cards}
      </body></html>`
    );
    win.document.close();
    win.print();
  };

  const headerBg = theme?.accent
    ? `${theme.accent}26`
    : 'rgba(25, 118, 210, 0.15)';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xl"
      aria-labelledby="clinical-browser-title"
      aria-modal="true"
      slotProps={{
        paper: {
          sx: {
            height: '90vh',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }
        }
      }}
    >
      {/* HEADER */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 2.5,
          py: 1.5,
          flexShrink: 0,
          backgroundColor: theme?.buttonStyle || '#f50057',
          color: theme?.buttonText || '#fff'
        }}
      >
        <Typography
          id="clinical-browser-title"
          variant="h6"
          sx={{ fontWeight: 'bold' }}
        >
          Clinical Records{patientLabel ? ` — ${patientLabel}` : ''}
        </Typography>
        <Box
          sx={{
            flex: 1
          }}
        />
        <Button
          size="small"
          startIcon={<SwapVertIcon />}
          onClick={() =>
            setSortOrder((s) => (s === 'newest' ? 'oldest' : 'newest'))
          }
          variant="outlined"
          sx={{
            color: '#fff',
            borderColor: 'rgba(255,255,255,0.5)',
            '&:hover': {
              borderColor: '#fff',
              backgroundColor: 'rgba(255,255,255,0.1)'
            }
          }}
        >
          {sortOrder === 'newest' ? 'Newest first' : 'Oldest first'}
        </Button>
        <IconButton
          onClick={handlePrint}
          size="small"
          aria-label="Print"
          sx={{ color: '#fff' }}
        >
          <PrintIcon />
        </IconButton>
        <IconButton
          onClick={onClose}
          size="small"
          aria-label="Close modal"
          sx={{ color: '#fff' }}
        >
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider />
      {/* BODY */}
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* SIDEBAR */}
        <Box
          sx={{
            width: 215,
            flexShrink: 0,
            borderRight: '1px solid',
            borderColor: 'divider',
            overflow: 'auto',
            p: 1.5,
            display: 'flex',
            flexDirection: 'column',
            gap: 0.25
          }}
        >
          <TextField
            inputRef={searchRef}
            size="small"
            placeholder="Search records…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            fullWidth
            sx={{ mb: 1 }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                )
              },

              htmlInput: { 'aria-label': 'Search records' }
            }}
          />

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 1,
              pt: 0.5,
              pb: 0.25
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontWeight: 'bold',
                color: 'text.secondary',
                fontSize: '0.875rem'
              }}
            >
              TYPE
            </Typography>
            <Tooltip
              title={
                filteredRecords.length > 0 &&
                filteredRecords.every((r) => expandedIds.has(r._id))
                  ? 'Collapse all'
                  : 'Expand all'
              }
            >
              <span>
                <IconButton
                  size="small"
                  disabled={filteredRecords.length === 0}
                  aria-label={
                    filteredRecords.length > 0 &&
                    filteredRecords.every((r) => expandedIds.has(r._id))
                      ? 'Collapse all records'
                      : 'Expand all records'
                  }
                  onClick={() => {
                    const allExpanded =
                      filteredRecords.length > 0 &&
                      filteredRecords.every((r) => expandedIds.has(r._id));
                    setExpandedIds(
                      allExpanded
                        ? new Set()
                        : new Set(filteredRecords.map((r) => r._id))
                    );
                  }}
                >
                  {filteredRecords.length > 0 &&
                  filteredRecords.every((r) => expandedIds.has(r._id)) ? (
                    <UnfoldLessIcon fontSize="small" />
                  ) : (
                    <UnfoldMoreIcon fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </Box>

          <SidebarItem
            label="All records"
            count={tiles.length}
            dotColor={typeFilter === null ? theme?.accent || '#1976d2' : '#bbb'}
            isSelected={typeFilter === null}
            onClick={() => setTypeFilter(null)}
          />
          {Object.entries(typeCounts)
            .sort((a, b) => b[1] - a[1])
            .map(([type, count]) => (
              <SidebarItem
                key={type}
                label={type}
                count={count}
                dotColor={NOTE_TYPE_COLOURS[type] || '#888'}
                isSelected={typeFilter === type}
                onClick={() => setTypeFilter(typeFilter === type ? null : type)}
              />
            ))}

          <Divider sx={{ my: 1 }} />

          <Typography
            variant="body2"
            sx={{
              fontWeight: 'bold',
              color: 'text.secondary',
              fontSize: '0.875rem',
              px: 1,
              pt: 0.5,
              pb: 0.25
            }}
          >
            PERIOD
          </Typography>

          <SidebarItem
            label="All time"
            isSelected={periodFilter === null}
            onClick={() => setPeriodFilter(null)}
          />
          {years.map((year) => (
            <SidebarItem
              key={year}
              label={String(year)}
              isSelected={periodFilter === String(year)}
              onClick={() =>
                setPeriodFilter(
                  periodFilter === String(year) ? null : String(year)
                )
              }
            />
          ))}
          {hasUndatedRecords && minYear && (
            <SidebarItem
              label={`Before ${minYear}`}
              isSelected={periodFilter === 'undated'}
              onClick={() =>
                setPeriodFilter(periodFilter === 'undated' ? null : 'undated')
              }
            />
          )}
        </Box>

        {/* RECORD LIST */}
        <Box
          role="list"
          aria-label="Clinical records list"
          sx={{ flex: 1, overflow: 'auto', p: 2 }}
        >
          {isLoading ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                pt: 6,
                color: 'text.secondary'
              }}
            >
              <Typography>Loading records…</Typography>
            </Box>
          ) : filteredRecords.length === 0 ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                pt: 6,
                color: 'text.secondary'
              }}
            >
              <Typography>No records found.</Typography>
            </Box>
          ) : (
            filteredRecords.map((record, index) => (
              <RecordCard
                key={record._id}
                record={record}
                isExpanded={expandedIds.has(record._id)}
                isFocused={focusedIndex === index}
                onToggle={toggleExpand}
                onFocus={() => setFocusedIndex(index)}
                cardRef={(el) => {
                  cardRefs.current[index] = el;
                }}
                dateFormat={dateFormat}
                staffMemberOptions={staffMemberOptions}
                carrier={carrier}
              />
            ))
          )}
        </Box>
      </Box>
      <Divider />
      {/* FOOTER */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 2.5,
          py: 1,
          flexShrink: 0,
          gap: 1
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary'
          }}
        >
          Showing {filteredRecords.length} record
          {filteredRecords.length !== 1 ? 's' : ''}
        </Typography>
        <Box
          sx={{
            flex: 1
          }}
        />
        <Button
          size="small"
          startIcon={<KeyboardArrowLeftIcon />}
          onClick={() => handlePrevNext('prev')}
          disabled={focusedIndex <= 0 || filteredRecords.length === 0}
        >
          Prev
        </Button>
        <Button
          size="small"
          endIcon={<KeyboardArrowRightIcon />}
          onClick={() => handlePrevNext('next')}
          disabled={
            focusedIndex >= filteredRecords.length - 1 ||
            filteredRecords.length === 0
          }
        >
          Next
        </Button>
      </Box>
    </Dialog>
  );
};

export default RecordBrowserModal;
